import dayjs from 'dayjs';
import {
  CustomFieldType,
  type CustomFieldFilterInput,
  type StudentImportRecordInput,
} from '@/types/composition-functions';
import type { CustomFieldFieldsFragment } from '@/types/operations';
import { isOriginAnswerName } from '@/utils/studentOrigin';

/**
 * Student origin/address fields and school custom fields in the bulk import
 * and the student list report (#574 phase 3). Pure logic, unit-tested in
 * studentDataFields.spec.ts.
 */

type CustomFieldDefinition = Pick<CustomFieldFieldsFragment, 'key' | 'label' | 'type' | 'options'>
  & Partial<Pick<CustomFieldFieldsFragment, 'is_required'>>;

export interface ImportField {
  key: string;
  label: string;
  required?: boolean;
  /**
   * A required custom field. The import does not enforce it (rows without it
   * still import), so it is flagged and warned about rather than blocking.
   */
  recommended?: boolean;
  /** Extra header spellings that auto-match this field. */
  aliases?: string[];
}

/** Prefix of an import column that fills a custom field: "custom:<key>". */
export const CUSTOM_IMPORT_PREFIX = 'custom:';

/** Optional profile columns the import accepts besides names, matric, email, DOB and gender. */
export const IMPORT_PROFILE_FIELDS: readonly ImportField[] = [
  { key: 'phone', label: 'Phone', aliases: ['phone number', 'mobile', 'telephone'] },
  { key: 'home_address', label: 'Residential Address', aliases: ['residential address', 'current address', 'address', 'contact address'] },
  { key: 'state', label: 'State of Residence', aliases: ['state of residence', 'residential state'] },
  { key: 'city', label: 'City', aliases: ['town'] },
  { key: 'nationality', label: 'Nationality', aliases: ['country'] },
];

export function customImportFields(fields: readonly CustomFieldDefinition[]): ImportField[] {
  return fields.map(f => ({
    key: `${CUSTOM_IMPORT_PREFIX}${f.key}`,
    label: `Custom: ${f.label}`,
    aliases: [f.label, f.key],
    ...(f.is_required ? { recommended: true } : {}),
  }));
}

/** Required custom fields with no column mapped — imported rows will be missing them. */
export function unmappedRecommendedFields(fields: readonly ImportField[], mapping: Record<string, string>): ImportField[] {
  return fields.filter(f => f.recommended && !mapping[f.key]);
}

const squash = (text: string) => text.toLowerCase().replace(/[^a-z0-9]/g, '');

function exactNames(field: ImportField): string[] {
  const label = field.label.replace(/^custom: /i, '');
  // The full label too, so the sample CSV's own "Custom: …" headers map back.
  return [field.key.replace(CUSTOM_IMPORT_PREFIX, ''), field.label, label, ...(field.aliases ?? [])].map(squash);
}

/**
 * Map fields to spreadsheet headers. Every exact match (key, label or alias,
 * ignoring case, spaces and punctuation) is claimed first; only then may a
 * built-in field fall back to a header that merely contains its key. Claimed
 * headers are never reused, so "State" cannot take a "State of Origin" column
 * that the State of Origin field matches exactly.
 */
export function autoMapImportHeaders(columns: readonly string[], fields: readonly ImportField[]): Record<string, string> {
  const mapping: Record<string, string> = {};
  const taken = new Set<string>();
  const claim = (field: ImportField, column: string | undefined) => {
    if (!column) return;
    mapping[field.key] = column;
    taken.add(column);
  };

  for (const field of fields) {
    const names = exactNames(field);
    claim(field, columns.find(c => !taken.has(c) && names.includes(squash(c))));
  }
  // The "contains" fallback skips custom fields and origin columns (#574): a
  // "State of Origin" column must not land in State of Residence.
  for (const field of fields) {
    if (mapping[field.key] || field.key.startsWith(CUSTOM_IMPORT_PREFIX)) continue;
    const key = squash(field.key);
    claim(field, columns.find(c => !taken.has(c) && !isOriginAnswerName(c) && squash(c).includes(key)));
  }

  return mapping;
}

const PROFILE_KEYS = ['surname', 'first_name', 'other_name', 'matric_no', 'email', 'dob', ...IMPORT_PROFILE_FIELDS.map(f => f.key)] as const;

/** One spreadsheet row (already mapped to field keys) as the importStudents input. */
export function buildImportRecord(row: Record<string, string>, subjects: string[]): StudentImportRecordInput {
  const record: Record<string, unknown> = {};
  for (const key of PROFILE_KEYS) {
    const value = (row[key] ?? '').trim();
    if (value) record[key] = value;
  }
  const gender = (row.gender ?? '').trim().toLowerCase();
  if (gender) record.gender = gender;

  const custom: Record<string, string> = {};
  for (const [key, value] of Object.entries(row)) {
    if (key.startsWith(CUSTOM_IMPORT_PREFIX) && (value ?? '').trim() !== '') {
      custom[key.slice(CUSTOM_IMPORT_PREFIX.length)] = value.trim();
    }
  }
  if (Object.keys(custom).length) record.custom_fields = custom;
  if (subjects.length) record.subjects = subjects;

  return record as StudentImportRecordInput;
}

/** Backend row-error key ("custom_fields.blood_group") → import field key ("custom:blood_group"). */
export function importErrorFieldKey(errorKey: string): string {
  return errorKey.startsWith('custom_fields.') ? `${CUSTOM_IMPORT_PREFIX}${errorKey.slice('custom_fields.'.length)}` : errorKey;
}

// ── Student list report: custom field filters ───────────────────────────────

export interface CustomFieldFilterRow {
  key: string | null;
  value: string | boolean | Date | null;
}

/** Filter rows → the API's [{key, value}], dropping incomplete rows. */
export function buildCustomFieldFilters(rows: readonly CustomFieldFilterRow[], fields: readonly CustomFieldDefinition[]): CustomFieldFilterInput[] {
  return rows.flatMap((row) => {
    const field = fields.find(f => f.key === row.key);
    if (!field || row.value === null || row.value === '') return [];

    let value: string;
    if (field.type === CustomFieldType.Checkbox) {
      value = row.value === true || row.value === 'true' ? 'true' : 'false';
    } else if (field.type === CustomFieldType.Date) {
      // dayjs keeps the local calendar date; toISOString() would shift it a day at UTC+1.
      value = row.value instanceof Date ? dayjs(row.value).format('YYYY-MM-DD') : String(row.value);
    } else {
      value = String(row.value).trim();
    }

    return value === '' ? [] : [{ key: field.key, value }];
  });
}
